import type { Tick, TransportMode } from "../types";
import { HistoryLoader } from "./HistoryLoader";
import { getRelay } from "./TickRelay";

const STREAM_SILENCE_MS = 12000;
const POLL_MS = 1200;
const STREAM_RETRY_MS = 30000;

export type TickSink = (ticks: Tick[]) => void;
export type StreamSource = "relay" | null;

// Live feed for ONE symbol. Source of truth: the Felacoo relay (backend's authenticated real-time Deriv stream).
// Fallback while the relay is silent/unavailable: short ticks_history polling through the backend proxy.
export class LiveFeed {
  mode: TransportMode = "connecting";
  source: StreamSource = null;
  private unsub: (() => void) | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private lastEpoch = 0;
  private stopped = false;

  constructor(readonly symbol: string, private sink: TickSink, private onMode: (m: TransportMode) => void) {
    this.startStream();
  }

  private setMode(m: TransportMode, source: StreamSource = null) {
    if (this.mode === m && this.source === source) return;
    this.mode = m; this.source = source;
    this.onMode(m);
  }

  private deliver(ticks: Tick[]) {
    const fresh = ticks.filter((t) => t.time > this.lastEpoch);
    if (!fresh.length) return;
    this.lastEpoch = fresh[fresh.length - 1].time;
    this.sink(fresh);
  }

  startStream() {
    if (this.stopped) return;
    this.stopStream();
    this.armSilence(() => this.fallbackToPolling());
    this.unsub = getRelay().subscribe(this.symbol, (ticks) => {
      this.armSilence(() => this.fallbackToPolling());
      if (this.source !== "relay") { this.stopPolling(); this.setMode("stream", "relay"); }
      this.deliver(ticks);
    });
  }

  private armSilence(onSilent: () => void) {
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
    this.silenceTimer = setTimeout(onSilent, STREAM_SILENCE_MS);
  }

  private stopStream() {
    if (this.silenceTimer) { clearTimeout(this.silenceTimer); this.silenceTimer = null; }
    if (this.unsub) { this.unsub(); this.unsub = null; }
  }

  private fallbackToPolling() {
    if (this.stopped) return;
    this.stopStream();
    this.setMode("polling");
    if (!this.pollTimer) {
      // Adaptive polling: quiet/closed markets back off up to 15s so dozens of watchlist symbols don't flood the proxy.
      let delay = POLL_MS;
      const poll = async () => {
        if (this.stopped || !this.pollTimer) return;
        try {
          const before = this.lastEpoch;
          this.deliver(await HistoryLoader.ticks(this.symbol, 25));
          delay = this.lastEpoch > before ? POLL_MS : Math.min(15000, delay * 2);
        } catch { delay = Math.min(15000, delay * 2); }
        if (this.pollTimer) this.pollTimer = setTimeout(poll, delay) as unknown as ReturnType<typeof setInterval>;
      };
      this.pollTimer = setTimeout(poll, 0) as unknown as ReturnType<typeof setInterval>;
    }
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => this.startStream(), STREAM_RETRY_MS);
  }

  private stopPolling() {
    if (this.pollTimer) { clearTimeout(this.pollTimer); this.pollTimer = null; }
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
  }

  stop() {
    this.stopped = true;
    this.stopStream();
    this.stopPolling();
  }
}
